/**
 * POST /api/v1/checkout, GET /api/v1/orders/{id}, GET /api/v1/orders/by-number/{no}.
 *
 * Checkout is the one call in the app that creates money-bearing state, so two
 * properties are non-negotiable here:
 *
 * 1. **An idempotency key on every attempt.** A double-tap, a flaky connection
 *    or a retry must not buy the same document twice. The key is derived from
 *    the quote, so the retry of a given purchase always carries the same one.
 * 2. **`expected_total_paise` is a check, not an instruction.** The server
 *    re-prices from the catalogue and refuses with `price_mismatch` if the two
 *    disagree. Sending it is how the customer's screen gets audited against the
 *    server, not how the amount is set.
 *
 * After a successful call the browser leaves for the gateway. Nothing about the
 * order's fate is decided here — the webhook settles that — so the return page
 * reads current state rather than trusting anything in the redirect.
 */
import { api } from './client';
import { queryKeys, useApiQuery } from './queryClient';
import type { CheckoutRequest, CheckoutResponse, OrderDetail, OrderList } from './types';

export const CHECKOUT_PATH = '/api/v1/checkout';
export const ORDERS_PATH = '/api/v1/orders';

/**
 * A stable key for one purchase attempt.
 *
 * Derived from the quote rather than random: the point is that a *retry* is
 * recognised as the same purchase. A fresh uuid per click would defeat the
 * mechanism precisely when it matters.
 */
export function checkoutIdempotencyKey(quoteId: string): string {
  return `checkout:${quoteId}`;
}

export function startCheckout(
  request: CheckoutRequest,
  signal?: AbortSignal,
): Promise<CheckoutResponse> {
  return api.post<CheckoutResponse>(CHECKOUT_PATH, request, {
    idempotencyKey: checkoutIdempotencyKey(request.quote_id),
    ...(signal ? { signal } : {}),
  });
}

export function fetchOrder(orderId: string, signal?: AbortSignal): Promise<OrderDetail> {
  return api.get<OrderDetail>(`${ORDERS_PATH}/${orderId}`, signal ? { signal } : undefined);
}

/**
 * The invoice PDF for a paid order.
 *
 * Fetched rather than linked. The endpoint authenticates with the customer's
 * access token, which lives in memory in lib/api/client.ts — a browser
 * following an `<a href>` sends cookies and no Authorization header, so the
 * link came back 401 and the customer got an error page where their invoice
 * should have been.
 */
export function fetchInvoice(orderId: string, signal?: AbortSignal): Promise<Blob> {
  return api.get<Blob>(`${ORDERS_PATH}/${encodeURIComponent(orderId)}/invoice.pdf`, {
    accept: 'application/pdf',
    asBlob: true,
    ...(signal ? { signal } : {}),
  });
}

export function fetchOrderByNumber(orderNo: string, signal?: AbortSignal): Promise<OrderDetail> {
  return api.get<OrderDetail>(
    `${ORDERS_PATH}/by-number/${encodeURIComponent(orderNo)}`,
    signal ? { signal } : undefined,
  );
}

/**
 * The order behind the number in the URL the gateway sent the customer back to.
 *
 * Polls while the order is still `awaiting_payment`: the customer's browser can
 * easily beat the webhook back, and "we have not heard from your bank yet" that
 * silently becomes "paid" a second later is the honest way to show that race.
 *
 * `enabled` exists for one specific reason. The access token lives in memory
 * only, so the round trip out to the payment gateway destroys it; on return the
 * session is rebuilt from the refresh cookie. Firing this query before that
 * finishes gets a 401 and shows a customer who has just paid "we could not find
 * that order". The caller must therefore hold it until the session has settled.
 */
export function useOrderByNumber(
  orderNo: string | undefined,
  { poll = true, enabled = true } = {},
) {
  return useApiQuery<OrderDetail, ReturnType<typeof queryKeys.orderByNumber>>({
    queryKey: queryKeys.orderByNumber(orderNo ?? ''),
    queryFn: ({ signal }) => fetchOrderByNumber(orderNo ?? '', signal),
    enabled: Boolean(orderNo) && enabled,
    staleTime: 0,
    refetchInterval: (query) => {
      if (!poll) return false;
      const status = query.state.data?.status;
      return status === 'awaiting_payment' ? 2000 : false;
    },
  });
}

/**
 * Every order this customer has placed, newest first.
 *
 * Only enabled once the session has settled, for the same reason the tracking
 * page waits: the access token is rebuilt from the refresh cookie on load, and
 * asking before that finishes gets a 401 and renders "we could not load your
 * orders" at someone who is perfectly signed in.
 */
export function useMyOrders(enabled = true, limit = 25) {
  return useApiQuery<OrderList, ReturnType<typeof queryKeys.myOrders>>({
    queryKey: queryKeys.myOrders(limit),
    queryFn: ({ signal }) =>
      api.get<OrderList>(`${ORDERS_PATH}?limit=${limit}`, signal ? { signal } : undefined),
    enabled,
    staleTime: 0,
  });
}
